import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

import { authStore, type DashboardAuth } from '../../auth/authStore';
import { CanvassingSessionExportStatus } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionExportFilter } from '../../graphql/__generated__/graphql';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';
import {
  canvassingSessionExportErrorCopy,
  getCanvassingSessionExport,
  getCanvassingSessionExportError,
  getLatestCanvassingSessionExport,
  startCanvassingSessionExport,
  type CanvassingSessionExportItem,
} from './canvassingSessionExportApi';

export const CANVASSING_SESSION_EXPORT_POLL_INTERVAL_MS = 5_000;

export type CanvassingSessionExportState = {
  latest: CanvassingSessionExportItem | null;
  latestLoading: boolean;
  latestError: string | null;
  startPending: boolean;
  startError: { message: string; uncertain: boolean } | null;
  pollingError: string | null;
};

type ScopedState = {
  auth: DashboardAuth | null;
  value: CanvassingSessionExportState;
};

export type UseCanvassingSessionExportOptions = {
  fetchImpl?: CanvassingSessionFetchImpl;
  scheduler?: CanvassingSessionExportPollingScheduler;
  visibilityTarget?: CanvassingSessionExportVisibilityTarget | null;
};

export type CanvassingSessionExportPollingScheduler = {
  setTimeout: (callback: () => void, delayMs: number) => ReturnType<typeof setTimeout>;
  clearTimeout: (timer: ReturnType<typeof setTimeout>) => void;
};

export type CanvassingSessionExportVisibilityTarget = Pick<
  Document,
  'visibilityState' | 'addEventListener' | 'removeEventListener'
>;

export type UseCanvassingSessionExportResult = {
  state: CanvassingSessionExportState;
  start: (filter: CanvassingSessionExportFilter) => Promise<void>;
  refreshLatest: () => void;
};

const subscribeAuth = (onChange: () => void) => authStore.subscribe(() => onChange());

const defaultScheduler: CanvassingSessionExportPollingScheduler = {
  setTimeout: (callback, delayMs) => setTimeout(callback, delayMs),
  clearTimeout: timer => clearTimeout(timer),
};

const defaultVisibilityTarget = (): CanvassingSessionExportVisibilityTarget | null =>
  typeof document === 'undefined' ? null : document;

const emptyState = (): CanvassingSessionExportState => ({
  latest: null,
  latestLoading: false,
  latestError: null,
  startPending: false,
  startError: null,
  pollingError: null,
});

const isRunning = (value: CanvassingSessionExportItem | null): boolean =>
  value?.status === CanvassingSessionExportStatus.Running;

export const useCanvassingSessionExport = ({
  fetchImpl,
  scheduler = defaultScheduler,
  visibilityTarget = defaultVisibilityTarget(),
}: UseCanvassingSessionExportOptions = {}): UseCanvassingSessionExportResult => {
  const auth = useSyncExternalStore(subscribeAuth, authStore.getAuth);
  const authRef = useRef(auth);
  authRef.current = auth;
  const fetchImplRef = useRef(fetchImpl);
  fetchImplRef.current = fetchImpl;
  const [scopedState, setScopedState] = useState<ScopedState>({
    auth,
    value: { ...emptyState(), latestLoading: auth !== null },
  });
  const mountedRef = useRef(false);
  const latestExportRef = useRef<CanvassingSessionExportItem | null>(null);
  const startControllerRef = useRef<AbortController | null>(null);
  const latestControllerRef = useRef<AbortController | null>(null);
  const startGenerationRef = useRef(0);
  const latestGenerationRef = useRef(0);
  const startPendingRef = useRef(false);

  const updateState = useCallback((requestAuth: DashboardAuth | null, update: (current: CanvassingSessionExportState) => CanvassingSessionExportState) => {
    if (authRef.current !== requestAuth) return;
    setScopedState(current =>
      current.auth === requestAuth ? { auth: requestAuth, value: update(current.value) } : current,
    );
  }, []);

  const loadLatest = useCallback(async (): Promise<void> => {
    const requestAuth = authRef.current;
    if (requestAuth === null) {
      updateState(requestAuth, () => emptyState());
      return;
    }

    latestControllerRef.current?.abort();
    const controller = new AbortController();
    const generation = ++latestGenerationRef.current;
    latestControllerRef.current = controller;
    updateState(requestAuth, current => ({ ...current, latestLoading: true, latestError: null }));

    const isCurrent = (): boolean =>
      mountedRef.current &&
      !controller.signal.aborted &&
      latestControllerRef.current === controller &&
      latestGenerationRef.current === generation &&
      authRef.current === requestAuth;

    try {
      const latest = await getLatestCanvassingSessionExport({ signal: controller.signal, fetchImpl: fetchImplRef.current });
      if (!isCurrent()) return;
      updateState(requestAuth, current => ({ ...current, latest, latestLoading: false, latestError: null }));
    } catch {
      if (!isCurrent()) return;
      updateState(requestAuth, current => ({
        ...current,
        latestLoading: false,
        latestError: 'Could not load the latest session export. Try again.',
      }));
    } finally {
      if (isCurrent()) latestControllerRef.current = null;
    }
  }, [updateState]);

  useEffect(() => {
    mountedRef.current = true;
    startControllerRef.current?.abort();
    latestControllerRef.current?.abort();
    startControllerRef.current = null;
    latestControllerRef.current = null;
    startPendingRef.current = false;
    setScopedState({ auth, value: { ...emptyState(), latestLoading: auth !== null } });
    void loadLatest();

    return () => {
      mountedRef.current = false;
      startGenerationRef.current += 1;
      latestGenerationRef.current += 1;
      startControllerRef.current?.abort();
      latestControllerRef.current?.abort();
      startControllerRef.current = null;
      latestControllerRef.current = null;
      startPendingRef.current = false;
    };
  }, [auth, loadLatest]);

  const state = scopedState.auth === auth ? scopedState.value : emptyState();
  const latest = state.latest;
  latestExportRef.current = latest;

  useEffect(() => {
    if (!isRunning(latest) || scopedState.auth !== auth) return undefined;

    let active = true;
    let inFlight = false;
    let requestGeneration = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let controller: AbortController | null = null;

    const isVisible = (): boolean => visibilityTarget?.visibilityState !== 'hidden';
    const clearTimer = (): void => {
      if (timer !== null) {
        scheduler.clearTimeout(timer);
        timer = null;
      }
    };
    const scheduleNext = (): void => {
      if (!active || inFlight || !isVisible()) return;
      clearTimer();
      timer = scheduler.setTimeout(() => {
        timer = null;
        requestStatus();
      }, CANVASSING_SESSION_EXPORT_POLL_INTERVAL_MS);
    };
    const requestStatus = (): void => {
      if (!active || inFlight || !isVisible() || latest === null) return;
      clearTimer();
      inFlight = true;
      const requestController = new AbortController();
      const generation = ++requestGeneration;
      controller = requestController;
      const isCurrent = (): boolean => {
        const currentExport = latestExportRef.current;
        return (
          active &&
          !requestController.signal.aborted &&
          generation === requestGeneration &&
          authRef.current === auth &&
          currentExport !== null &&
          currentExport.id === latest.id &&
          currentExport.status === CanvassingSessionExportStatus.Running
        );
      };

      void getCanvassingSessionExport({ id: latest.id, signal: requestController.signal, fetchImpl: fetchImplRef.current })
        .then(nextExport => {
          if (!isCurrent()) return;
          controller = null;
          inFlight = false;
          if (nextExport === null) {
            updateState(auth, current => ({ ...current, pollingError: 'Could not refresh export status. Try again.' }));
            scheduleNext();
            return;
          }
          updateState(auth, current => ({ ...current, latest: nextExport, pollingError: null }));
          if (nextExport.status === CanvassingSessionExportStatus.Running) scheduleNext();
        })
        .catch(() => {
          if (!isCurrent()) return;
          controller = null;
          inFlight = false;
          updateState(auth, current => ({ ...current, pollingError: 'Could not refresh export status. Try again.' }));
          scheduleNext();
        });
    };
    const handleVisibilityChange = (): void => {
      if (!isVisible()) {
        clearTimer();
        requestGeneration += 1;
        const currentController = controller;
        controller = null;
        inFlight = false;
        // This aborts only the status read; accepted export work continues on the server.
        currentController?.abort();
        return;
      }
      if (!inFlight) requestStatus();
    };

    visibilityTarget?.addEventListener('visibilitychange', handleVisibilityChange);
    scheduleNext();
    return () => {
      active = false;
      clearTimer();
      visibilityTarget?.removeEventListener('visibilitychange', handleVisibilityChange);
      requestGeneration += 1;
      const currentController = controller;
      controller = null;
      inFlight = false;
      currentController?.abort();
    };
  }, [auth, latest?.id, latest?.status, scopedState.auth, scheduler, updateState, visibilityTarget]);

  const start = useCallback(async (filter: CanvassingSessionExportFilter): Promise<void> => {
    if (startPendingRef.current || authRef.current === null) return;

    const requestAuth = authRef.current;
    const controller = new AbortController();
    const generation = ++startGenerationRef.current;
    startControllerRef.current = controller;
    startPendingRef.current = true;
    updateState(requestAuth, current => ({ ...current, startPending: true, startError: null }));

    const isCurrent = (): boolean =>
      mountedRef.current &&
      !controller.signal.aborted &&
      startControllerRef.current === controller &&
      startGenerationRef.current === generation &&
      authRef.current === requestAuth;

    try {
      const acceptedExport = await startCanvassingSessionExport({ filter, signal: controller.signal, fetchImpl: fetchImplRef.current });
      if (!isCurrent()) return;
      updateState(requestAuth, current => ({ ...current, latest: acceptedExport, pollingError: null }));
    } catch (error: unknown) {
      if (!isCurrent()) return;
      const requestError = getCanvassingSessionExportError(error);
      updateState(requestAuth, current => ({
        ...current,
        startError: {
          message: canvassingSessionExportErrorCopy(requestError),
          uncertain: requestError.uncertain,
        },
      }));
    } finally {
      if (isCurrent()) {
        startControllerRef.current = null;
        startPendingRef.current = false;
        updateState(requestAuth, current => ({ ...current, startPending: false }));
      }
    }
  }, [updateState]);

  return { state, start, refreshLatest: () => void loadLatest() };
};
